import { makeStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import { BoxWrapper, Button, Modal, Prompt } from 'components/Base';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import ModelPortfolioCreator from './ModelPortfolioCreator';
import ModelPortfolioLookup from './ModelPortfolioLookup';
import selectors from 'store/selectors';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '576px',
    minHeight: '300px',
    alignItems: 'center',
    paddingBottom: '2rem'
  },
  creatorContent: {
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column'
  },
  mpsRow: {
    paddingLeft: '2rem',
    paddingRight: '2rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 2rem',
    fontSize: '0.8rem',
    lineHeight: '1rem',
    color: '#747474'
  },
  checkboxLabel: {
    fontSize: '10px'
  }
});

interface ModelPortfolioSelectorProps {
  isOpen: boolean;
  closeModelFunc: (val: boolean) => void;
  addModelPortfolioSelection?: Function;
  investments: any;
  option?: string;
  modelPortfolioId?: number;
  date?: string;
  cipCategories?: any[];
  addingModelPortfolioSelection?: boolean;
  hideDate?: boolean;
  onClone?: Function | null;
  onCreate?: Function | null;
  canHaveHistory?: boolean;
  canLock?: boolean;
  children?: any;
  defaults?: any;
}

const ModelPortfolioSelector = ({
  isOpen,
  closeModelFunc,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addModelPortfolioSelection = () => {},
  investments,
  option = '',
  modelPortfolioId,
  date,
  cipCategories,
  addingModelPortfolioSelection = false,
  onClone = null,
  onCreate = null,
  canHaveHistory = true,
  canLock = true,
  children,
  defaults = null
}: ModelPortfolioSelectorProps) => {
  const classes = useStyles();
  const [selectedOption, setSelectedOption] = useState(option);
  const [isModalHidden, setIsModalHidden] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isClosePromptOpen, setIsClosePromptOpen] = useState(false);

  const forceClose = () => {
    setIsClosePromptOpen(false);
    closeModelFunc(false);
    setSelectedOption('');
  };

  const close = (force = false) => {
    if (hasChanges && !force) {
      setIsClosePromptOpen(true);
      return;
    }

    forceClose();
  };

  useEffect(() => {
    if (option && isOpen) {
      setSelectedOption(option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getTitle = () => {
    if (selectedOption === '') return 'Add a Model Portfolio';
    if (selectedOption === 'create') return 'Create Custom Portfolio';
    if (selectedOption === 'select') return 'Select a Model Portfolio';
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModalFunc={closeModelFunc}
      isInvisible={isModalHidden}
    >
      <Formik
        enableReinitialize
        initialValues={{
          cip_categories: cipCategories,
          category: '',
          modelPortfolioSelection: ''
        }}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={() => {}}
      >
        {({ handleSubmit }: FormikProps<any>) => {
          return (
            <BoxWrapper title={getTitle()} closeModal={close}>
              <div
                style={{
                  width: selectedOption === 'create' ? '80vw' : '576px',
                  height: selectedOption === 'create' ? '85vh' : 'auto',
                  overflow: 'hidden'
                }}
              >
                {selectedOption !== 'create' && (
                  <form onSubmit={handleSubmit}>
                    {selectedOption.length === 0 && (
                      <div className={classes.content}>
                        <>
                          <p>Would you like to</p>
                          <Button
                            color="primary"
                            onClick={() => setSelectedOption('select')}
                          >
                            Select from our list of model portfolios
                          </Button>
                          <p>or</p>
                          <Button
                            color="primary"
                            onClick={() => setSelectedOption('create')}
                          >
                            Create a model portfolio
                          </Button>
                        </>
                      </div>
                    )}
                    {selectedOption === 'select' && (
                      <ModelPortfolioLookup
                        addModelPortfolioSelection={addModelPortfolioSelection}
                        investments={investments}
                        addingModelPortfolioSelection={
                          addingModelPortfolioSelection
                        }
                        onFinish={close}
                      />
                    )}
                  </form>
                )}

                {selectedOption === 'create' && (
                  <ModelPortfolioCreator
                    date={date}
                    modelPortfolioId={modelPortfolioId}
                    addModelPortfolioSelection={addModelPortfolioSelection}
                    setHasChanges={setHasChanges}
                    hasChanges={hasChanges}
                    close={close}
                    setIsModalHidden={setIsModalHidden}
                    onClone={onClone}
                    investments={investments}
                    canHaveHistory={canHaveHistory}
                    canLock={canLock}
                    onCreate={onCreate}
                    defaults={defaults}
                  >
                    {children}
                  </ModelPortfolioCreator>
                )}
              </div>
            </BoxWrapper>
          );
        }}
      </Formik>
      <Prompt
        isOpen={isClosePromptOpen}
        title="You have outstanding changes, are you sure you want to leave?"
        confirmAction={() => forceClose()}
        cancelAction={() => setIsClosePromptOpen(false)}
        isProcessing={false}
      />
    </Modal>
  );
};

export default connect(
  (state: any) => ({
    cipCategories: selectors.cipSelectors.getCipCategories(state)
  }),
  null
)(ModelPortfolioSelector);
